import { useCallback, useRef } from 'react';
import { afterMove } from '../engine/preparedStates';
import { meleeReminder } from '../engine/roster/melee';
import { infoLine } from './shellCombatant';

const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * useShellMoveRules - what a finished move does to the mover (2026-10-26), on the client that made it, for every
 * move (a hidden figure's too, whose line the map never writes): a run leaves him Ran, moving loses a held aim and
 * spends a Tactical Advance, and leaving melee without a Disengage writes a reminder. Lines about a hidden mover
 * are never written: they would name him to the table.
 *
 * @param {object} s - roster, ruleTokens, hidden, sync (the tracker), updateCombatantState, addRollNarrative.
 * @returns {(facts: object) => void} the map's `onMoveSettled`
 */
export default function useShellMoveRules(s) {
  const live = useRef(s);
  live.current = s;
  return useCallback((facts) => {
    const { roster, ruleTokens, hidden, sync, updateCombatantState, addRollNarrative } = live.current;
    const mover = roster?.find((c) => c.id === facts?.actorId);
    if (!mover) return;
    const trackerOn = !!sync?.turnTrackerState?.active;
    const before = mover.state?.conditions ?? [];
    const out = afterMove(before, facts.mode, trackerOn);
    if (!sameList(out.conds, before)) {
      updateCombatantState(mover.id, (st) => ({
        ...st,
        conditions: afterMove(st.conditions ?? [], facts.mode, trackerOn).conds,
      }));
    }
    const hiddenIds = hidden?.hiddenIds;
    if (hiddenIds?.has(mover.id)) return;
    const name = mover.sheet?.name ?? mover.id;
    if (out.removed.length) addRollNarrative(infoLine(`⏱ ${out.removed.join(' and ')} lost - ${name} moved.`));
    const reminder = meleeReminder(facts, mover, { roster, tokens: ruleTokens, hiddenIds });
    if (reminder) addRollNarrative(infoLine(reminder));
  }, []);
}
